import { useTranslation } from 'react-i18next';
import React from 'react';

import { Lng } from '@/i18n/resources';
import collegialLogo from '@/assets/logos/collegial_logo_stack.svg';
import { LanguagePicker } from '@/component/Footer/LanguagePicker';
import {
  Background,
  FooterWrapper,
  FooterLayout,
  LogoLink,
  CollegialLogo,
  TopNavigationGroupStyled,
  Support,
  Copyright,
  LinksTop,
  LinksBottom,
  TextLink,
  AppVersion,
  ExternalLink,
  DividerStyled,
} from '@/component/Footer/styles';
import { Layout } from '@/ui/Layout';
import { CURRENT_YEAR } from '@/utils/currentYear';
import { Button } from '@/ui/Button';
import { ROUTE_PATH } from '@/constants/routePaths';

export const Footer = ({
  appVersion,
  showLanguagePicker,
  currentLanguage,
  languageChangePending,
  setLanguage,
  isInPreviewModal = false,
  showFeedbackButton = false,
  onFeedbackClick,
}: {
  appVersion: string;
  showLanguagePicker: boolean;
  currentLanguage: Lng;
  setLanguage: (nextLanguage: Lng) => void;
  languageChangePending: boolean;
  isInPreviewModal?: boolean;
  showFeedbackButton?: boolean;
  onFeedbackClick?: () => void;
}): JSX.Element => {
  const { t } = useTranslation('footer');
  return (
    <Background>
      <Layout.ContentSection>
        <FooterWrapper>
          <FooterLayout.Top>
            <TopNavigationGroupStyled>
              <LogoLink target={isInPreviewModal ? '_blank' : '_self'} to="/">
                <CollegialLogo src={collegialLogo} alt={t('Collegial logo')} />
              </LogoLink>
              <LinksTop>
                <TextLink target={isInPreviewModal ? '_blank' : '_self'} to="/">
                  {t('Home', { ns: 'navbar' })}
                </TextLink>
                <TextLink target={isInPreviewModal ? '_blank' : '_self'} to={ROUTE_PATH.MY_LEARNING_ONGOING}>
                  {t('My learning', { ns: 'navbar' })}
                </TextLink>
              </LinksTop>
            </TopNavigationGroupStyled>
            <Support>
              {showFeedbackButton && onFeedbackClick && (
                <Button
                  size="large"
                  icon={['far', 'comment']}
                  $iconPosition="start"
                  variant="secondary"
                  onClick={onFeedbackClick}
                  type="button"
                >
                  Give Feedback
                </Button>
              )}
            </Support>
          </FooterLayout.Top>
          <DividerStyled />
          <FooterLayout.Bottom>
            <Copyright>© {CURRENT_YEAR} Collegial</Copyright>
            <LinksBottom>
              <TextLink target={isInPreviewModal ? '_blank' : '_self'} to={ROUTE_PATH.ABOUT}>
                {t('About us', { ns: 'about' })}
              </TextLink>
              <TextLink target={isInPreviewModal ? '_blank' : '_self'} to={ROUTE_PATH.TERMS}>
                {t('Terms of Use', { ns: 'onboarding' })}
              </TextLink>
              <TextLink target={isInPreviewModal ? '_blank' : '_self'} to={ROUTE_PATH.PRIVACY}>
                {t('Privacy Notes', { ns: 'onboarding' })}
              </TextLink>
              <ExternalLink target="_blank" rel="noopener noreferrer" href="https://support.collegial.com/support/solutions">
                {t('Knowledge base')}
              </ExternalLink>
              {showLanguagePicker && (
                <LanguagePicker
                  currentLanguage={currentLanguage}
                  setLanguage={setLanguage}
                  languageChangePending={languageChangePending}
                />
              )}
            </LinksBottom>
          </FooterLayout.Bottom>
        </FooterWrapper>
      </Layout.ContentSection>
      <AppVersion>{appVersion}</AppVersion>
    </Background>
  );
};
